// Web-side glue for the shared product-binding engine: maps the generated
// product DTO into the PickedProduct payload buildProductApplyPatches
// consumes, and re-exports the engine against the editor's (Orval-typed)
// SceneDocument so call sites don't repeat the cast.

import type { SceneNode, SceneDocument } from '@/lib/scene-document';
import type {
  SceneDocument as SharedSceneDocument,
} from '@nexmail/shared-util';
import type { ProductVariantSearchResultDto } from '@/apis/backend/generated/models';

import {
  type PickedProduct,
  type ProductSnapshot,
  type ProductApplyOptions,
  findProductBlockRoot as sharedFindRoot,
  readProductSnapshot as sharedReadSnapshot,
  buildProductApplyPatches as sharedBuildPatches,
} from '@nexmail/shared-util/email-builder';

export type { PickedProduct, ProductSnapshot };

export function pickedProductFromDto(p: ProductVariantSearchResultDto): PickedProduct {
  return {
    productId: p.productId,
    variantId: p.variantId,
    name: p.productTitle ?? '',
    price: p.variantPrice != null ? String(p.variantPrice) : undefined,
    compareAtPrice: p.variantCompareAtPrice != null ? String(p.variantCompareAtPrice) : undefined,
    // Cutout preference is resolved server-side at send/agent time; the
    // editor uses the catalog photo and lets the cached-cutout path upgrade.
    transparentImageUrl: undefined,
    fallbackImageUrl: p.variantImageUrl ?? p.productImages?.[0]?.src ?? '',
    productUrl: p.productUrl ?? '',
  };
}

export function findProductBlockRoot(doc: SceneDocument, nodeId: string): string | null {
  return sharedFindRoot(doc as unknown as SharedSceneDocument, nodeId);
}

// A price-looking string ("$245.00", "245,00", "€19"), used to recognize an
// untagged price node by its content when its name doesn't say so.
const PRICE_RE = /(?:[$£€¥]\s?\d|\d[.,]\d{2})/;
// Card frames are named "Product", "Product 1", "Product 2"... by the section
// templates. We deliberately do NOT match container names like "Product feed"
// so a swap targets one card, not the whole grid.
const PRODUCT_CARD_NAME_RE = /^product(\s*\d+)?$/i;

function nodeName(n: SceneNode | undefined): string {
  return (n?.name ?? '').trim();
}

function subtreeHasImage(doc: SceneDocument, rootId: string): boolean {
  const n = doc.nodes[rootId];
  if (!n) return false;
  if (n.type === 'IMAGE') return true;
  return (n.children ?? []).some((id) => subtreeHasImage(doc, id));
}

/**
 * Infer which product attribute each descendant of a card renders, for cards
 * that carry no productField tags yet (section-template product cards come in
 * as plain frames named "Product N" with "Photo"/"Title"/"Price" children).
 * Name is the strongest signal; price content and node type are fallbacks.
 */
export function inferProductRoles(
  doc: SceneDocument,
  rootId: string,
): Map<string, NonNullable<SceneNode['productField']>> {
  const roles = new Map<string, NonNullable<SceneNode['productField']>>();
  const visit = (id: string, isRoot: boolean) => {
    const n = doc.nodes[id];
    if (!n) return;
    // A nested bound block is a different product — don't reach into it.
    if (!isRoot && n.productBinding) return;
    // The root is the card container, never a field — only its descendants
    // render product attributes. (The card is often named "Product 1", which
    // would otherwise read as a title.)
    if (!isRoot) {
      const name = nodeName(n).toLowerCase();
      let role: NonNullable<SceneNode['productField']> | null = null;
      if (n.type === 'IMAGE' || /\b(photo|image|picture)\b/.test(name)) {
        role = 'image';
      } else if (n.type === 'TEXT' && /compare|was\b|old\b|original|msrp/.test(name)) {
        role = 'compareAtPrice';
      } else if (n.type === 'TEXT' && /price|amount|cost/.test(name)) {
        role = 'price';
      } else if (/\b(cta|button|shop|buy|order)\b/.test(name)) {
        role = 'cta';
      } else if (n.type === 'TEXT' && /title|name|heading/.test(name)) {
        role = 'title';
      } else if (n.type === 'TEXT' && PRICE_RE.test(n.text ?? '')) {
        role = 'price';
      }
      // Deliberately no "any leftover text → title" fallback: it would mislabel
      // badges ("New") or CTA labels ("Shop Now"). Templates name their title
      // node, so a name/price-content signal is required.
      // One node per role: keep the first (top-down) candidate for each.
      if (role && ![...roles.values()].includes(role)) {
        roles.set(id, role);
      }
    }
    for (const childId of n.children ?? []) visit(childId, false);
  };
  visit(rootId, true);
  return roles;
}

function isProductCardRoot(doc: SceneDocument, node: SceneNode): boolean {
  // Already a real product block, or a structurally-detected card from import.
  if (node.productBinding || node.pluginData?.productCard === true) return true;
  if (node.type !== 'FRAME' && node.type !== 'GROUP') return false;
  // Template cards: named "Product N" and actually holding an image.
  if (PRODUCT_CARD_NAME_RE.test(nodeName(node)) && subtreeHasImage(doc, node.id)) return true;
  // Generic shape: a card whose direct children read as image + title/price.
  const kids = (node.children ?? []).map((id) => doc.nodes[id]).filter(Boolean) as SceneNode[];
  const hasTitle = kids.some((k) => /title|name|heading/i.test(nodeName(k)));
  const hasImage = kids.some((k) => k.type === 'IMAGE' || /photo|image/i.test(nodeName(k)));
  const hasPrice = kids.some(
    (k) => /price/i.test(nodeName(k)) || (k.type === 'TEXT' && PRICE_RE.test(k.text ?? '')),
  );
  return hasTitle && (hasImage || hasPrice);
}

/**
 * Nearest ancestor-or-self that is a product CARD root — a bound block
 * (`productBinding`), an import-stamped card (`pluginData.productCard`), or a
 * structural section-template card (named "Product N" with image + title/price).
 *
 * Powers the on-canvas "Replace product" affordance: unlike findProductBlockRoot
 * (binding-only) this also matches untagged cards, and the swap binds them on
 * first use — the same bind-on-drop behavior the DnD path gives a plain image.
 */
export function findProductCardRoot(doc: SceneDocument, nodeId: string): string | null {
  let current: SceneNode | undefined = doc.nodes[nodeId];
  while (current) {
    if (isProductCardRoot(doc, current)) return current.id;
    current = current.parentId ? doc.nodes[current.parentId] : undefined;
  }
  return null;
}

export function buildProductApplyPatches(
  doc: SceneDocument,
  rootId: string,
  product: PickedProduct,
  opts?: ProductApplyOptions,
): Array<{ nodeId: string; props: Partial<SceneNode> }> {
  return sharedBuildPatches(
    doc as unknown as SharedSceneDocument,
    rootId,
    product,
    opts,
  ) as unknown as Array<{ nodeId: string; props: Partial<SceneNode> }>;
}

/**
 * Swap a card to a product whether or not it was already a bound product block.
 *
 * Tagged cards (productField present) go straight through the shared engine.
 * Untagged section-template cards get their roles inferred by name/type and the
 * productField tags are merged into the patches alongside the value rewrites —
 * so one undoable step both fills the card AND promotes it to a real product
 * block (binding, snapshot, links), making future swaps/refresh/AI work too.
 */
export function buildProductSwapPatches(
  doc: SceneDocument,
  rootId: string,
  product: PickedProduct,
  opts?: ProductApplyOptions,
): Array<{ nodeId: string; props: Partial<SceneNode> }> {
  const roles = inferProductRoles(doc, rootId);
  const alreadyTagged = [...roles.keys()].some((id) => doc.nodes[id]?.productField);
  if (alreadyTagged || roles.size === 0) {
    // Card is already a product block (or we can't read its structure): let the
    // shared engine rewrite the productField-tagged nodes as-is.
    return buildProductApplyPatches(doc, rootId, product, opts);
  }
  // Tag a working copy so the engine can find the field nodes, then fold the
  // tags back into the emitted patches so they persist on the real document.
  const tagged: SceneDocument = { ...doc, nodes: { ...doc.nodes } };
  for (const [id, role] of roles) {
    tagged.nodes[id] = { ...doc.nodes[id], productField: role } as SceneNode;
  }
  const enginePatches = buildProductApplyPatches(tagged, rootId, product, opts);
  const byId = new Map(enginePatches.map((p) => [p.nodeId, { ...p.props }]));
  for (const [id, role] of roles) {
    const props = byId.get(id) ?? {};
    (props as Partial<SceneNode>).productField = role;
    byId.set(id, props);
  }
  return [...byId].map(([nodeId, props]) => ({ nodeId, props }));
}

export function readProductSnapshot(node: SceneNode | undefined): ProductSnapshot | null {
  return sharedReadSnapshot(node as never);
}

/**
 * First product-block root inside a subtree, in child (visual) order. Used
 * after inserting a library product section to rebind its lead card to the
 * product the user actually clicked.
 */
export function findFirstBoundBlockInSubtree(doc: SceneDocument, rootId: string): string | null {
  const node = doc.nodes[rootId];
  if (!node) return null;
  if (node.productBinding) return node.id;
  for (const childId of node.children ?? []) {
    const found = findFirstBoundBlockInSubtree(doc, childId);
    if (found) return found;
  }
  return null;
}

/**
 * First product CARD root inside a subtree, in child (visual) order — bound,
 * import-stamped, OR structural (section-template cards that arrive untagged).
 * Used after inserting a library product section to prefill its lead card with
 * the clicked product even when the backend didn't stamp binding. Stops at the
 * first card so it doesn't descend into the card's own field nodes.
 */
export function findFirstProductCardInSubtree(doc: SceneDocument, rootId: string): string | null {
  const node = doc.nodes[rootId];
  if (!node) return null;
  if (isProductCardRoot(doc, node)) return node.id;
  for (const childId of node.children ?? []) {
    const found = findFirstProductCardInSubtree(doc, childId);
    if (found) return found;
  }
  return null;
}
